import { compareWeeks, type RowComparison, type WeekRow } from "./weekDelta";
import { fetchAssignedWorkoutTree, type AssignedWorkoutTree, type Rpc, type TreeExercise } from "./assignedWorkoutTree";

/* WHAT NEXT WEEK WOULD BE — the web PROPOSES, the phone WRITES (W3, his decision 2026-09-18).
 *
 * 🔴 **THIS IS DELIBERATELY NOT A PROGRESSION ENGINE, AND THAT IS THE WHOLE DESIGN.**
 * `CoachProgression.plan` and `CoachNextWeek.bind` are a pure rules engine in Swift, harness-
 * compiled so every claim about them is checkable — which sets pair with which, what counts as
 * consent, how a flat prescription goes back onto rows that split it, in what ORDER statements
 * must issue. Re-implementing any of that here would be a SECOND set of rules, and his own
 * compatibility rule forbids exactly that: *breadth may differ, the MODEL may not.*
 *
 * ⚠️ **SO THIS PROPOSES NOTHING OF ITS OWN. IT READS.** It takes the week a client was given and
 * the week before it, and says what the coach can already see if they open two screens and hold
 * them side by side. Every number here came off a row; none was computed by a rule.
 *
 * ⚠️ **AND THE LIVE CONSEQUENCE IS STATED RATHER THAN SOFTENED:** the writer's ONLY trigger is a
 * coach opening the iOS Clients screen, so a coach who lives on the web never gets a week written
 * for them at all. This screen does not fix that; it makes the gap visible and usable.
 */

/** One row of a week, flattened out of the tree the RPC returns. */
export function flattenWeek(tree: AssignedWorkoutTree): WeekRow[] {
  const out: WeekRow[] = [];
  const sections = [...(tree.coach_workout_sections ?? [])]
    // ⚠️ A PostgREST embed arrives UNORDERED — `CLAUDE.md`'s own trap, and the reason `position`
    // is asked for at all. Without this a coach's cool-down could be proposed first.
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  for (const section of sections) {
    const exercises = [...(section.coach_workout_exercises ?? [])]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    for (const ex of exercises) {
      out.push(...rowsOf(ex, section.section_type ?? null));
    }
  }
  return out;
}

/**
 * ⚠️ **ONE ROW PER SET-GROUP, NOT ONE PER EXERCISE, and the distinction is his week 5's shape.**
 * A top set and a rested back-off are authored as TWO set rows at two loads under one exercise;
 * collapsing them to an average would describe a prescription nobody wrote, and taking the first
 * would silently drop the back-off. Sets that agree on every number are one row with a count.
 */
function rowsOf(ex: TreeExercise, role: string | null): WeekRow[] {
  const sets = [...(ex.coach_workout_sets ?? [])]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  if (sets.length === 0) {
    return [{
      exerciseId: ex.exercise_id ?? ex.id,
      role,
      sets: null, reps: null, weightKg: null, holdSeconds: null,
      setIds: [], exerciseRowId: ex.id,
    }];
  }

  const groups: { key: string; count: number; set: (typeof sets)[number]; ids: string[] }[] = [];
  for (const s of sets) {
    const key = `${s.reps ?? "-"}|${numeric(s.weight_kg) ?? "-"}|${s.time_seconds ?? "-"}`;
    const last = groups[groups.length - 1];
    if (last !== undefined && last.key === key) { last.count += 1; last.ids.push(s.id); }
    else groups.push({ key, count: 1, set: s, ids: [s.id] });
  }

  return groups.map((g) => ({
    exerciseId: ex.exercise_id ?? ex.id,
    role,
    sets: g.count,
    reps: g.set.reps,
    weightKg: numeric(g.set.weight_kg),
    holdSeconds: g.set.time_seconds,
    setIds: g.ids,
    exerciseRowId: ex.id,
  }));
}

/** `weight_kg` comes back as a string from PostgREST's numeric. ⚠️ An unparseable value is NULL,
 *  never 0 — "nobody recorded it" and "unloaded" are different facts, and this repo has paid for
 *  confusing them. */
function numeric(v: number | string | null): number | null {
  if (v === null) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export type Proposal = {
  /** The week the proposal is built FROM — the most recent one the client was given. */
  fromLabel: string;
  comparisons: RowComparison[];
};

/**
 * What the coach would be repeating if next week were a copy of the last one.
 *
 * ⚠️ **IT COMPARES THE TWO WEEKS THAT EXIST — it does not invent a third.** The proposal IS last
 * week, and the deltas shown are what moved between the two weeks already on the calendar. A
 * coach reading it can see whether the last step was taken and decide the next one; nothing here
 * decides it for them.
 */
export function proposal(
  previous: AssignedWorkoutTree | null,
  latest: AssignedWorkoutTree | null,
): Proposal | null {
  if (latest === null) return null;
  const now = flattenWeek(latest);
  const before = previous === null ? [] : flattenWeek(previous);
  return { fromLabel: latest.name, comparisons: compareWeeks(before, now) };
}

/** How many rows actually moved — the number the heading carries. */
export function movedCount(p: Proposal): number {
  return p.comparisons.filter(
    (c) => c.kind === "new" || c.kind === "dropped"
      || (c.kind === "changed" && c.deltas.length > 0),
  ).length;
}


/**
 * WHAT NEXT WEEK CHANGES, PAIRED BY TEMPLATE — the This week board's "N changes proposed" and the client
 * Week's proposals overlay (W72 steps 1–2; the copied `NextWeekPanel` and its `readProposal` are gone).
 *
 * 🔴 NOT "the latest dated workout vs the one before", AND THE REASON WAS MEASURED ON THE BOARD'S OWN
 * STILL: that pairing compares whatever two workouts were dated last, which for a client with a Push
 * day and a Pull day is Push against Pull — every row "new", and the board printed "35 changes
 * proposed" about a week nobody had adjusted. The weekly adjustment moves a TEMPLATE's numbers from one
 * week to the next, so each assignment after this Sunday is compared with the most recent earlier copy
 * of the SAME template — `coalesce(root_workout_id, workout_id)`, the identity
 * `coach_assignments_one_source_per_day_idx` already uses. An assignment with no earlier copy is a new
 * workout, not an adjustment, and counts nothing.
 */
export function nextWeekPairs(
  assignments: readonly { workoutId: string; rootId: string | null; scheduledDateISO: string | null }[],
  afterISO: string,
): { latest: string; previous: string }[] {
  const dated = assignments.filter((a) => a.scheduledDateISO !== null);
  const out: { latest: string; previous: string }[] = [];
  for (const a of dated) {
    const day = (a.scheduledDateISO ?? "").slice(0, 10);
    if (day <= afterISO) continue;
    const key = a.rootId ?? a.workoutId;
    const earlier = dated
      .filter((b) => (b.rootId ?? b.workoutId) === key && (b.scheduledDateISO ?? "").slice(0, 10) <= afterISO)
      .sort((x, y) => (y.scheduledDateISO ?? "").localeCompare(x.scheduledDateISO ?? ""))[0];
    if (earlier !== undefined) out.push({ latest: a.workoutId, previous: earlier.workoutId });
  }
  return out;
}

/** The rows next week moves, summed over its template pairs; null when a tree could not be read. */
export async function readNextWeekChanges(
  rpc: Rpc,
  pairs: readonly { latest: string; previous: string }[],
): Promise<number | null> {
  let moved = 0;
  for (const pair of pairs) {
    const [latest, previous] = await Promise.all([
      fetchAssignedWorkoutTree(rpc, pair.latest), fetchAssignedWorkoutTree(rpc, pair.previous),
    ]);
    if (latest.tree === null || previous.tree === null) return null;
    const p = proposal(previous.tree, latest.tree);
    if (p !== null) moved += movedCount(p);
  }
  return moved;
}
